import Image from "next/image";
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import { Vehicle } from "@/data/vehicles";
import type { Locale } from "@/config/i18n";
import { buildLocationBookingHref } from "./locationBooking";
import {
  formatEuro,
  getRentalPriceTiers,
  RENTAL_DURATION_OPTIONS,
} from "./pricing";
import { VEHICLES_UI, localizeGearbox, vehiclesBasePath } from "./vehiclesUi";
import { tr } from "@/config/localeText";

export default function VehicleCard({
  vehicle,
  locale = "fr",
}: {
  vehicle: Vehicle;
  locale?: Locale;
}) {
  const ui = tr(VEHICLES_UI, locale);
  const c = ui.card;
  const rentalTiers = getRentalPriceTiers(vehicle);
  const ficheHref = `${vehiclesBasePath(locale)}/${vehicle.id}`;

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-primaryLoc/15 bg-white shadow-[0_4px_14px_rgba(79,21,21,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(79,21,21,0.12)]">
      <Link
        href={ficheHref}
        aria-label={c.detailsAria(vehicle.name)}
        className="block"
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <Image
            src={vehicle.images.main}
            alt={vehicle.name}
            fill
            className="object-cover transition duration-300 group-hover:scale-[1.02]"
          />
          <div className="absolute left-3 top-3 flex items-center gap-2">
            {vehicle.isNew && (
              <span className="rounded-full bg-primaryLoc px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-gold-100">
                {c.newBadge}
              </span>
            )}
            {vehicle.withDriver && (
              <span className="rounded-full bg-gold-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#3b0e0e]">
                {c.driverBadge}
              </span>
            )}
          </div>
          <div className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-xs leading-none text-white backdrop-blur-sm">
            <Icon
              icon="material-symbols:photo-camera-outline"
              className="h-4 w-4"
            />
            <span className="leading-none">
              {vehicle.images.gallery.length}
            </span>
          </div>
        </div>

        <div className="space-y-3 p-3.5 sm:p-4">
          <div className="flex items-start justify-between gap-3">
            <h3 className="line-clamp-1 text-base font-bold text-[#3b0e0e] sm:text-lg">
              {vehicle.name}
            </h3>
            <span className="shrink-0 rounded-full border border-primaryLoc/20 bg-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-primaryLoc/80">
              {vehicle.brand}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-primaryLoc/80">
            <div className="border-primaryLoc/12 flex min-w-0 items-center gap-1.5 rounded-lg border bg-secondary/45 px-2.5 py-2">
              <Icon
                icon="material-symbols:speed-outline"
                className="h-4 w-4 shrink-0"
              />
              <span className="truncate">{vehicle.specs.engine}</span>
            </div>
            <div className="border-primaryLoc/12 flex min-w-0 items-center gap-1.5 rounded-lg border bg-secondary/45 px-2.5 py-2">
              <Icon
                icon="material-symbols:settings-outline"
                className="h-4 w-4 shrink-0"
              />
              <span className="truncate">
                {localizeGearbox(vehicle.specs.gearbox, locale)}
              </span>
            </div>
            <div className="border-primaryLoc/12 col-span-2 flex min-w-0 items-center gap-1.5 rounded-lg border bg-secondary/45 px-2.5 py-2">
              <Icon
                icon="material-symbols:palette-outline"
                className="h-4 w-4 shrink-0"
              />
              <span className="truncate">{vehicle.specs.exteriorColor}</span>
            </div>
          </div>
        </div>
      </Link>

      <div className="mt-auto border-t border-primaryLoc/10 px-3.5 py-3 sm:px-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {rentalTiers ? (
            <div className="flex-1 space-y-2">
              <div className="grid grid-cols-3 gap-1 text-[10px] text-primaryLoc/85 sm:text-[11px]">
                {RENTAL_DURATION_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className="rounded-md border border-primaryLoc/15 bg-secondary/35 px-1.5 py-1"
                  >
                    <p className="truncate text-[9px] uppercase tracking-[0.02em] text-primaryLoc/70 sm:text-[10px]">
                      {ui.durationLabels[option.value]}
                    </p>
                    <p className="font-semibold text-primaryLoc">
                      {formatEuro(rentalTiers[option.value])}
                      {c.perDay}
                    </p>
                  </div>
                ))}
              </div>
              {vehicle.price.km_limit && (
                <p className="text-[10px] text-primaryLoc/65 sm:text-[11px]">
                  {c.kmPerDay(vehicle.price.km_limit)}
                </p>
              )}
            </div>
          ) : (
            <span className="whitespace-nowrap text-sm font-bold text-primaryLoc sm:text-base">
              {c.fromHourly(formatEuro(vehicle.price.amount))}
            </span>
          )}

          <div className="flex items-center justify-end gap-2">
            <Link
              href={buildLocationBookingHref(vehicle.id, locale)}
              className="inline-flex h-9 items-center justify-center overflow-hidden whitespace-nowrap rounded-lg border border-primaryLoc/50 bg-white px-3 text-sm font-semibold text-primaryLoc transition-all duration-200 hover:border-primaryLoc hover:bg-primaryLoc hover:text-white md:w-0 md:border-transparent md:px-0 md:opacity-0 md:group-focus-within:w-[96px] md:group-focus-within:border-primaryLoc/50 md:group-focus-within:px-3 md:group-focus-within:opacity-100 md:group-hover:w-[96px] md:group-hover:border-primaryLoc/50 md:group-hover:px-3 md:group-hover:opacity-100"
            >
              {c.reserve}
            </Link>
            <Link
              href={ficheHref}
              aria-label={c.detailsAria(vehicle.name)}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primaryLoc text-white transition-transform group-hover:translate-x-0.5"
            >
              <Icon icon="material-symbols:arrow-forward" className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
